import { history } from '~/store';

import { REDIRECT_ORDER_DRAWER_REQUEST_STORE } from '../constants/redirectOrderDrawerRequestStore.const';
import { REDIRECT_AND_OPEN_ORDER_DRAWER_SCAN_ACTION_NAME } from '../constants/scanActionNames.const';
import { type RedirectOrderDrawerRequestStore } from '../types/redirectOrderDrawerRequest.type';
import { type ScanAction, type ScanActionResult } from '../types/scanAction.type';
import { type ScanEvent } from '../types/scanEvent.type';
import { resolveDisplayedOrderDetailsOrderId } from '../utils/resolveOrderDetailsPageRoute.util';

interface CreateRedirectAndOpenOrderDrawerScanActionOptions {
  navigate?: (path: string) => void;
  requestStore?: RedirectOrderDrawerRequestStore;
}

const isRedirectAndOpenOrderDrawerMatch = (scanEvent: ScanEvent): boolean => {
  const { barcodeContent, isConfiguredForScanToPrint, route } = scanEvent.scanContext;
  if (!isConfiguredForScanToPrint || !barcodeContent) return false;

  if (route.route === 'shipments') {
    /* There are subpages under `/shipments/*`, such as `/shipments/manifests`.
    That said, we should check the `rawPath` and execute the action when it's different from `/shipments` (the root page). */
    const isShipmentsRoot = route.rawPath === '/shipments' || route.rawPath === '/shipments/';
    if (!isShipmentsRoot) return true;

    return !barcodeContent.transactionObjectId;
  }

  if (route.route !== 'orders') return true;

  if (route.rawPath === '/orders' || route.rawPath === '/orders/') return false;

  return resolveDisplayedOrderDetailsOrderId(route.rawPath) !== barcodeContent.orderObjectId;
};

export const createRedirectAndOpenOrderDrawerScanAction = ({
  navigate = (path) => history.push(path),
  requestStore = REDIRECT_ORDER_DRAWER_REQUEST_STORE,
}: CreateRedirectAndOpenOrderDrawerScanActionOptions = {}): ScanAction => ({
  execute: (scanEvent: ScanEvent): ScanActionResult => {
    const barcodeContent = scanEvent.scanContext.barcodeContent;
    if (!barcodeContent) throw new Error('Missing barcode content for redirected order drawer');

    requestStore.set({ barcodeContent, correlationId: scanEvent.correlationId });

    try {
      navigate('/orders');
    } catch (error) {
      requestStore.reset();
      throw error;
    }

    return { success: true };
  },

  matches: isRedirectAndOpenOrderDrawerMatch,

  name: REDIRECT_AND_OPEN_ORDER_DRAWER_SCAN_ACTION_NAME,
});
